import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge } from "./Dashboard";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, MailCheck, BadgeCheck, Trash2, Star, Check, X, DollarSign, CheckCircle2 } from "lucide-react";
import { copyToClipboardWithToast } from "@/lib/clipboard";
import { getTenantSlug } from "@/lib/tenant";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import { BookIllustration } from "@/components/admin/EditorialIllustrations";
import { DisplayHeadline, GoldRule, GoldFoilButton, HairlineButton } from "@/components/editorial";
import LottieEditorial from "@/components/editorial/LottieEditorial";
import calendarFlipLottie from "@/assets/lottie/calendar-flip.json";
import { EDITORIAL_ASSETS } from "@/lib/editorialAssets";

const FILTERS = ["all", "pending", "confirmed", "completed", "cancelled"];

/* Inline triage pills — render the right next-action(s) on each booking row so
   the photographer can confirm or cancel without opening the menu. Status →
   relevant pills mapping (mirrors the DropdownMenu but only shows the actions
   that ACTUALLY make sense for the current state):
     pending     → Confirm · Cancel
     confirmed   → (Mark deposit if not yet) · Mark complete · Cancel
     completed   → (no pills — terminal)
     cancelled   → Reactivate (→ pending)
   Each pill uses the same partial-PATCH the menu items use, so backend code is
   unchanged. Optimistic update keeps the UI snappy for batch triage. */
const PILL_PALETTE = {
  success: "border-emerald-500/40 text-emerald-700 bg-emerald-500/10 hover:bg-emerald-500/20",
  danger: "border-rose-500/40 text-rose-700 bg-rose-500/10 hover:bg-rose-500/20",
  warning: "border-amber-500/40 text-amber-700 bg-amber-500/10 hover:bg-amber-500/20",
  neutral: "border-border text-muted-foreground bg-background hover:bg-muted/40",
};

function Pill({ tone, icon: Icon, label, testid, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1 px-2.5 h-7 text-[10px] uppercase tracking-[0.18em] border transition-colors ${PILL_PALETTE[tone]}`}
    >
      <Icon className="w-3 h-3" />
      {label}
    </button>
  );
}

function QuickActions({ booking, onPatch, layout = "" }) {
  const { status, deposit_received } = booking;
  // Layout suffix keeps test ids unique across mobile <ul> + desktop <table>
  // since CSS only hides — both branches render in the DOM. Passes 'mobile' or
  // 'desktop' from the call site; default empty for ad-hoc consumers.
  const sfx = layout ? `-${layout}` : "";
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {status === "pending" && (
        <>
          <Pill tone="success" icon={Check} label="Confirm" testid={`confirm-${booking.reference}${sfx}`} onClick={() => onPatch(booking.id, { status: "confirmed" })} />
          <Pill tone="danger" icon={X} label="Cancel" testid={`cancel-${booking.reference}${sfx}`} onClick={() => onPatch(booking.id, { status: "cancelled" })} />
        </>
      )}
      {status === "confirmed" && (
        <>
          {!deposit_received && (
            <Pill tone="warning" icon={DollarSign} label="Deposit paid" testid={`deposit-${booking.reference}${sfx}`} onClick={() => onPatch(booking.id, { deposit_received: true })} />
          )}
          <Pill tone="success" icon={CheckCircle2} label="Complete" testid={`complete-${booking.reference}${sfx}`} onClick={() => onPatch(booking.id, { status: "completed" })} />
          <Pill tone="danger" icon={X} label="Cancel" testid={`cancel-${booking.reference}${sfx}`} onClick={() => onPatch(booking.id, { status: "cancelled" })} />
        </>
      )}
      {status === "cancelled" && (
        <Pill tone="neutral" icon={Check} label="Reactivate" testid={`reactivate-${booking.reference}${sfx}`} onClick={() => onPatch(booking.id, { status: "pending" })} />
      )}
    </div>
  );
}

export default function AdminBookings() {
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const tenantSlug = getTenantSlug() || "";

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/bookings?status_filter=${filter}`);
      setItems(data);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  async function updateBooking(id, patch) {
    try {
      const { data } = await api.patch(`/admin/bookings/${id}`, patch);
      setItems((it) => it.map((b) => (b.id === id ? data : b)));
      toast.success("Booking updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  async function sendReminder(id) {
    try {
      await api.post(`/admin/bookings/${id}/send-reminder`);
      toast.success("Reminder sent");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  async function sendFeedbackRequest(id) {
    if (!window.confirm("Email a feedback link to this client now?")) return;
    try {
      await api.post(`/admin/bookings/${id}/send-feedback-request`);
      toast.success("Feedback request sent");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this booking?")) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      setItems((it) => it.filter((b) => b.id !== id));
      toast.success("Booking deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  return (
    <div className="space-y-8" data-testid="admin-bookings-page">
      <AdminPageHeader
        breadcrumb={["WORKFLOW", "BOOKINGS"]}
        title="Bookings"
        count={items.length}
        primary={{ label: "New booking", href: "/booking?admin=1", testid: "bookings-new-booking" }}
        secondary={{ label: "Open calendar", href: "/admin/availability", testid: "bookings-open-calendar" }}
      />

      {/* Hairline filter row — sits beneath the page header */}
      <div className="flex flex-wrap items-center gap-x-7 gap-y-2 -mt-2 pb-2 border-b border-[color:var(--gold-foil)]/20" data-testid="bookings-filter-row">
        <span className="ed-eyebrow text-muted-foreground/70">Filter</span>
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`filter-${f}`}
            className={`relative pb-2 text-[11px] uppercase tracking-[0.22em] transition-colors ${
              filter === f ? "text-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {f}
            {filter === f && <span className="absolute left-0 right-0 -bottom-px h-[1.5px] bg-[color:var(--gold-base)]" />}
          </button>
        ))}
      </div>

      {/* Editorial empty state — only when there are no bookings AT ALL on the 'all' filter */}
      {!loading && items.length === 0 && filter === "all" && (
        <div className="relative mx-auto max-w-xl text-center py-16 px-6" data-testid="bookings-empty-state">
          {/* Phase 1 — editorial backdrop plate (atelier scene) sits behind
              the copy at low opacity. Lottie reveals will layer on top of
              this in Phase 4. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 -z-10 overflow-hidden"
            style={{
              backgroundImage: `linear-gradient(rgba(14,13,12,0.84), rgba(14,13,12,0.95)), url(${EDITORIAL_ASSETS.editorial_atelier_scene.src})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              opacity: 0.9,
            }}
          />
          <div className="mx-auto mb-8 inline-flex items-center justify-center">
            <LottieEditorial
              animation={calendarFlipLottie}
              size={140}
              loop={false}
              autoplay
              reducedMotionFallback={
                <span className="inline-flex items-center justify-center w-16 h-16 text-[color:var(--gold-base)]">
                  <BookIllustration />
                </span>
              }
              data-testid="bookings-empty-lottie"
            />
          </div>
          <p className="ed-eyebrow text-[color:var(--gold-base)] mb-5">Awaiting your first session</p>
          <DisplayHeadline as="h2" size="section" align="center" className="mb-5">
            The book is <em>unopened</em>.
          </DisplayHeadline>
          <GoldRule width="3rem" align="center" className="my-5" />
          <p className="ed-body-lg max-w-md mx-auto font-serif italic text-muted-foreground">
            When clients request a sitting, their bookings will appear here — in chronological order, ready for confirmation.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-5">
            <GoldFoilButton
              onClick={() => navigate("/booking?admin=1")}
              tone="dark"
              data-testid="bookings-empty-primary"
            >
              Add your first booking
            </GoldFoilButton>
            <HairlineButton
              onClick={() => {
                const url = `${window.location.origin}/booking?brand=${tenantSlug}`;
                copyToClipboardWithToast(url, "Booking link copied to clipboard");
              }}
              tone="dark"
              arrow={false}
              size="sm"
              data-testid="bookings-empty-share-link"
            >
              or share your booking link →
            </HairlineButton>
          </div>
        </div>
      )}

      {/* Mobile: stacked cards */}
      <ul className="lg:hidden bg-card border border-border divide-y divide-border" data-testid="bookings-mobile-list">
        {loading && (<li className="px-5 py-10 text-center text-muted-foreground">Loading…</li>)}
        {!loading && items.length === 0 && (<li className="px-5 py-10 text-center text-muted-foreground">No bookings.</li>)}
        {items.map((b) => (
          <li key={b.id} className="p-4 space-y-2" data-testid={`booking-card-${b.reference}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{b.customer_name}</div>
                <div className="text-xs text-muted-foreground truncate">{b.customer_email}</div>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-2 -mt-1 -mr-1 hover:bg-muted" data-testid={`booking-menu-${b.reference}`}><MoreHorizontal className="w-4 h-4" /></button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => updateBooking(b.id, { status: "confirmed" })}>Mark confirmed</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => updateBooking(b.id, { deposit_received: true })}>Mark deposit received</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => updateBooking(b.id, { status: "completed" })}>Mark completed</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => updateBooking(b.id, { status: "cancelled" })}>Cancel</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => sendReminder(b.id)}><MailCheck className="w-4 h-4 mr-2" /> Send reminder</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => sendFeedbackRequest(b.id)}><Star className="w-4 h-4 mr-2" /> Send feedback request</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => remove(b.id)} className="text-destructive"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="text-sm">
              <div>{b.service_name}</div>
              <div className="text-xs text-muted-foreground">{b.tier_name}</div>
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span>{b.session_date} · {b.session_time}</span>
              <span>·</span>
              <span>${b.tier_price.toFixed(0)}</span>
              <span>·</span>
              <span className="font-mono">{b.reference}</span>
            </div>

            <div className="flex items-center gap-2 pt-1 flex-wrap">
              <StatusBadge value={b.status} />
              {b.deposit_received ? (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-emerald-400"><BadgeCheck className="w-3.5 h-3.5" /> Deposit paid</span>
              ) : (
                <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Deposit pending</span>
              )}
            </div>

            <div className="pt-1">
              <QuickActions booking={b} onPatch={updateBooking} layout="mobile" />
            </div>
          </li>
        ))}
      </ul>

      {/* Desktop: full table */}
      <div className="hidden lg:block bg-card border border-border overflow-x-auto">
        <table className="w-full text-sm" data-testid="bookings-table">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="px-5 py-3 eyebrow font-normal">Ref</th>
              <th className="px-5 py-3 eyebrow font-normal">Client</th>
              <th className="px-5 py-3 eyebrow font-normal">Service</th>
              <th className="px-5 py-3 eyebrow font-normal">Date</th>
              <th className="px-5 py-3 eyebrow font-normal">Total</th>
              <th className="px-5 py-3 eyebrow font-normal">Deposit</th>
              <th className="px-5 py-3 eyebrow font-normal">Status</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={8} className="px-5 py-10 text-center text-muted-foreground">Loading…</td></tr>)}
            {!loading && items.length === 0 && (<tr><td colSpan={8} className="px-5 py-10 text-center text-muted-foreground">No bookings.</td></tr>)}
            {items.map((b) => (
              <tr key={b.id} className="border-t border-border" data-testid={`booking-row-${b.reference}`}>
                <td className="px-5 py-4 font-mono text-xs">{b.reference}</td>
                <td className="px-5 py-4">
                  <div>{b.customer_name}</div>
                  <div className="text-xs text-muted-foreground">{b.customer_email}</div>
                </td>
                <td className="px-5 py-4">
                  <div>{b.service_name}</div>
                  <div className="text-xs text-muted-foreground">{b.tier_name}</div>
                </td>
                <td className="px-5 py-4">{b.session_date}<div className="text-xs text-muted-foreground">{b.session_time}</div></td>
                <td className="px-5 py-4">${b.tier_price.toFixed(0)}</td>
                <td className="px-5 py-4">{b.deposit_received ? <BadgeCheck className="w-4 h-4 text-emerald-400" /> : <span className="text-muted-foreground">Pending</span>}</td>
                <td className="px-5 py-4"><StatusBadge value={b.status} /></td>
                <td className="px-5 py-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <QuickActions booking={b} onPatch={updateBooking} layout="desktop" />
                    <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="p-2 hover:bg-muted" data-testid={`booking-menu-${b.reference}`}><MoreHorizontal className="w-4 h-4" /></button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => updateBooking(b.id, { status: "confirmed" })}>Mark confirmed</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => updateBooking(b.id, { deposit_received: true })}>Mark deposit received</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => updateBooking(b.id, { status: "completed" })}>Mark completed</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => updateBooking(b.id, { status: "cancelled" })}>Cancel</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => sendReminder(b.id)}><MailCheck className="w-4 h-4 mr-2" /> Send reminder</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => sendFeedbackRequest(b.id)} data-testid={`send-feedback-${b.id}`}><Star className="w-4 h-4 mr-2" /> Send feedback request</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => remove(b.id)} className="text-destructive"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
